/* AiReviewHelpers.js
 *
 * Helper functions for AI review logic, including outcome determination,
 * text processing, reviewer information handling, and popup caution shaping.
 */

import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import ThumbsUpDownRoundedIcon from "@mui/icons-material/ThumbsUpDownRounded";
import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded";
import { ModelType } from "../models";

export function getOutcome(score, confidence) {
  if (confidence === "high") return "ai_holds";
  if (confidence === "medium") return score === 3 ? "needs_review" : "ai_holds";
  return "needs_review";
}

export function getReviewType(score, confidence) {
  return getOutcome(score, confidence) === "ai_holds"
    ? "A single confirming review"
    : "Full review process";
}

export function getOutcomeLabel(score, confidence) {
  return getOutcome(score, confidence) === "ai_holds"
    ? "Follow AI advice"
    : "Needs normal review";
}

export function getAiRecommendationLabel(score, confidence) {
  return getOutcome(score, confidence) === "ai_holds"
    ? "Single review"
    : "Full review";
}

export function isEmptyObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0;
}

export function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export function getNestedPropertyValue(object, path) {
  if (!object || typeof object !== "object" || !path || typeof path !== "string") return undefined;

  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object") return acc[key];
    return undefined;
  }, object);
}

export function htmlToPlainTextPreserveBlocks(html) {
  if (!html || typeof html !== "string") return "";

  const root = document.createElement("div");
  root.innerHTML = html;

  const BLOCK_AFTER = new Set([
    "P",
    "DIV",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "LI",
    "UL",
    "OL",
    "BLOCKQUOTE",
    "PRE",
    "SECTION",
    "ARTICLE",
    "HEADER",
    "FOOTER",
  ]);

  let out = "";

  const walk = (node) => {
    if (!node) return;

    if (node.nodeType === Node.TEXT_NODE) {
      out += node.nodeValue || "";
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName;

      if (tag === "BR") {
        out += "\n";
        return;
      }

      Array.from(node.childNodes || []).forEach(walk);

      if (BLOCK_AFTER.has(tag)) out += "\n\n";
    }
  };

  walk(root);

  let text = out.replace(/\r/g, "");
  text = text.replace(/[ \t\f\v]+/g, " ");
  text = text
    .split("\n")
    .map((line) => line.trim())
    .join("\n");
  text = text.replace(/\n{3,}/g, "\n\n").trim();

  return text;
}

export function truncateText(str, maxLen) {
  const s = (str || "").trim();
  if (!s) return "";
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

export function getConfidenceTier(confidence) {
  if (confidence === "high") return "high";
  if (confidence === "medium") return "medium";
  return "low";
}

export function getQualityTier(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "unknown";
  if (n >= 4) return "high";
  if (n === 3) return "medium";
  return "low";
}

export function tierLabel(tier) {
  if (tier === "high") return "High";
  if (tier === "medium") return "Medium";
  if (tier === "low") return "Low";
  return "—";
}

export function tierIcon(tier) {
  if (tier === "high") return ThumbUpAltRoundedIcon;
  if (tier === "medium") return ThumbsUpDownRoundedIcon;
  return ThumbDownAltRoundedIcon;
}

export function getReviewReviewerId(review) {
  if (review?.userId != null) return String(review.userId);
  if (review?.meta?.owner?.userId != null) return String(review.meta.owner.userId);
  return "";
}

// The only correct way to resolve a review's linked submission: a `linkedData`
// entry with modelType === ModelType.SUBMISSION, read via its `linkedTo` field.
// No fallbacks to legacy shapes (data.submission, direct submissionId fields, etc).
export function getLinkedSubmissionId(review) {
  const linkedData = Array.isArray(review?.linkedData) ? review.linkedData : [];
  const entry = linkedData.find((item) => item?.modelType === ModelType.SUBMISSION);
  return entry?.linkedTo ? String(entry.linkedTo) : "";
}

export function getSubmissionId(row) {
  if (row?.id != null) return String(row.id);
  return "";
}

export function getReviewProgress(review) {
  const rawPercent = getNestedPropertyValue(review, "meta.percentComplete");
  const percent = Number(rawPercent);
  const safePercent = Number.isFinite(percent) ? clamp(percent, 0, 100) : 0;
  const isSubmitted = getNestedPropertyValue(review, "meta.complete") === true || safePercent >= 100;

  if (isSubmitted) {
    return {
      label: "Submitted",
      percent: 100,
      tone: "complete",
    };
  }

  if (safePercent > 0) {
    return {
      label: "In progress",
      percent: safePercent,
      tone: "progress",
    };
  }

  return {
    label: "Assigned",
    percent: 0,
    tone: "idle",
  };
}

export function buildReviewerDisplayName(reviewer) {
  if (!reviewer || typeof reviewer !== "object") return "Unknown reviewer";

  const givenName = reviewer.givenName ? String(reviewer.givenName).trim() : "";
  const familyName = reviewer.familyName ? String(reviewer.familyName).trim() : "";
  const fullName = `${givenName} ${familyName}`.trim();

  if (fullName) return fullName;
  if (reviewer.email) return String(reviewer.email);
  if (reviewer.id) return String(reviewer.id);

  return "Unknown reviewer";
}

export function buildReviewerSecondaryLabel(reviewer) {
  if (!reviewer || typeof reviewer !== "object") return "";
  if (reviewer.email) return String(reviewer.email);
  if (reviewer.id) return String(reviewer.id);
  return "";
}

export function normalizeReviewersForAssignment(reviewers) {
  return (Array.isArray(reviewers) ? reviewers : []).map((reviewer) => {
    const tokens = Array.isArray(reviewer?.tokens) ? reviewer.tokens.map(String) : [];
    const hasReviewerToken = tokens.includes("HHHRRR");

    return {
      ...reviewer,
      reviewer: reviewer?.reviewer === true || hasReviewerToken,
    };
  });
}

export function confidenceLabel(value) {
  if (value === "high") return "High confidence";
  if (value === "medium") return "Medium confidence";
  if (value === "low") return "Low confidence";
  return "Unknown confidence";
}

export function buildTrustWarnings({
  specialistReviewNeeded = false,
  sectionBreakdown = null,
  structuralSuggestions = [],
  structureScore = null,
  titleAlignment = null,
  vaguenessFlags = null,
}) {
  const warnings = [];

  if (specialistReviewNeeded) {
    warnings.push({
      key: "specialist",
      tone: "medium",
      title: "Specialist review required",
      body: "The AI has flagged this as a specialist case. Treat the AI route as guidance and confirm reviewer fit carefully.",
    });
  }

  const missing = Array.isArray(sectionBreakdown?.missing) ? sectionBreakdown.missing : [];
  if (missing.includes("methods") || missing.includes("results")) {
    warnings.push({
      key: "missing_sections",
      tone: "medium",
      title: "Structural sections may be missing",
      body: "Methods and/or Results may not have been parsed cleanly. Verify formatting before relying on the structural readout.",
    });
  }

  if (typeof structureScore === "number" && structureScore < 0.55) {
    warnings.push({
      key: "structure_score",
      tone: "medium",
      title: "Low structural confidence",
      body: "The abstract structure appears weak or incomplete. Use caution when interpreting the AI summary.",
    });
  }

  if (typeof titleAlignment === "number" && titleAlignment < 0.55) {
    warnings.push({
      key: "title_alignment",
      tone: "medium",
      title: "Weak title alignment",
      body: "The title may not align strongly with the abstract body. Check thematic fit before reviewer assignment.",
    });
  }

  const flaggedSentenceCount = Array.isArray(vaguenessFlags?.flaggedSentences)
    ? vaguenessFlags.flaggedSentences.length
    : 0;

  if (flaggedSentenceCount > 2) {
    warnings.push({
      key: "vagueness",
      tone: "medium",
      title: "Multiple vague statements detected",
      body: "Some abstract sentences may be too vague for confident interpretation. Review the submission manually if needed.",
    });
  }

  if (Array.isArray(structuralSuggestions) && structuralSuggestions.length > 0) {
    warnings.push(
      ...structuralSuggestions.slice(0, 3).map((suggestion, index) => ({
        key: `suggestion_${index}`,
        tone: "neutral",
        title: "Structural cue",
        body: suggestion,
      }))
    );
  }

  return warnings;
}

export function getPrimaryTrustWarning(trustWarnings = []) {
  return Array.isArray(trustWarnings) && trustWarnings.length > 0 ? trustWarnings[0] : null;
}

export function classifyRecommendationFit(value) {
  const safeValue = Number(value);
  if (!Number.isFinite(safeValue)) return "neutral";
  if (safeValue >= 75) return "high";
  if (safeValue >= 45) return "medium";
  return "low";
}

export function formatClassificationName(item) {
  if (!item || typeof item !== "object") return "";
  if (typeof item.name === "string" && item.name.trim()) return item.name.trim();
  if (typeof item.value === "string" && item.value.trim()) {
    return item.value
      .trim()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (s) => s.toUpperCase());
  }
  return "";
}
